import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import EducationDrawer from 'components/EducationDrawer';
import { HelpButton } from 'components/EducationDrawer/StyledComponents';
import EmojiPicker from 'components/EmojiPicker';
import { AttachmentUpload } from 'components/FormFields';
import MessagePreview from '../MessagePreview';
import {
  ContentGrid,
  LeftColumn,
  RightColumn,
  ChannelSection,
  ChannelTabs,
  ChannelTab,
  ComposeSection,
  TitleInput,
  MessageTextarea,
  CharacterCount,
  QuickVariables,
  VariableButton,
} from './StyledComponents';

const CodeBlock = styled.code`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.2)'
      : 'rgba(250, 70, 22, 0.1)'};
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 13px;
`;

const MessageComposer = ({
  selectedChannel,
  setSelectedChannel,
  message,
  setMessage,
  pushTitle,
  setPushTitle,
  variables = [],
  customPlaceholder,
  replaceVariables,
  context = 'campaign', // 'campaign' or 'credit'
  attachments,
  setAttachments,
  availableChannels = ['SMS', 'Push'], // New prop to control which channels are available
  onCharacterCountChange, // New callback for character count data
  onInsertVariable, // New callback to expose insertVariableAtCursor functionality
  hidePreview = false, // New prop to hide the preview column
  additionalSection = null, // New prop to render additional content between Channel and Compose
  onError = null, // New prop to handle errors from child components
  hideCharacterCount = false, // New prop to hide the character count and segment info
}) => {
  const theme = useTheme();
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [showVariableEducation, setShowVariableEducation] = useState(false);
  const messageTextareaRef = useRef(null);
  const emojiButtonRef = useRef(null);

  // Auto-expand textarea as user types
  useEffect(() => {
    const textarea = messageTextareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set the height to the scrollHeight (content height)
      // Math.min ensures we don't exceed a maximum height (400px)
      textarea.style.height = Math.min(textarea.scrollHeight, 400) + 'px';
    }
  }, [message]); // Re-run when message changes

  // Get platform-specific events URL
  const eventsUrl =
    process.env.REACT_APP_PLATFORM === 'pitpay'
      ? 'https://pitpay.com/events'
      : 'https://tickethoss.com/events';

  // Check if message contains characters outside GSM 7-bit alphabet (requires Unicode encoding)
  const hasUnicode = (text) => {
    if (!text) return false;

    // GSM 7-bit default alphabet and extended characters (including digits 0-9)
    const gsm7bitRegex =
      /^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ\x1B^{}\\[~\]|€ÆæßÉ !"#¤%&'()*+,\-./:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà0123456789\s]*$/; // eslint-disable-line no-control-regex

    // If text contains characters outside GSM 7-bit, it needs Unicode
    return !gsm7bitRegex.test(text);
  };

  // Calculate SMS segments based on encoding
  const calculateSMSSegments = (text, isMMSMessage = false) => {
    if (!text || text.length === 0) return 1; // Minimum 1 segment for empty message

    // MMS has different limits
    if (isMMSMessage) {
      // MMS supports up to 1600 characters
      return Math.ceil(text.length / 1600);
    }

    const isUnicode = hasUnicode(text);

    // Character limits per Twilio documentation
    const singleSmsLength = isUnicode ? 70 : 160;
    const multiSmsLength = isUnicode ? 67 : 153;

    if (text.length <= singleSmsLength) {
      return 1;
    } else {
      return Math.ceil(text.length / multiSmsLength);
    }
  };

  // Character counting logic
  const charactersUsed = (message || '').length;
  const hasAttachments = attachments && attachments.length > 0;
  // No longer treating as MMS since we're using links
  const isMMSMessage = false;
  const isUnicodeMessage =
    selectedChannel === 'SMS' && hasUnicode(message || '');

  // Determine character limit based on message type
  let characterLimit;
  if (selectedChannel === 'Push') {
    characterLimit = 250; // Push notification limit
  } else if (isUnicodeMessage) {
    // Unicode SMS: First segment 70, subsequent segments 67
    characterLimit =
      charactersUsed <= 70 ? 70 : 67 * Math.ceil(charactersUsed / 67);
  } else {
    // Standard GSM-7 SMS: First segment 160, subsequent segments 153
    characterLimit =
      charactersUsed <= 160 ? 160 : 153 * Math.ceil(charactersUsed / 153);
  }

  // Calculate segments
  const segments =
    selectedChannel === 'SMS' ? calculateSMSSegments(message || '', false) : 0;

  // Credit calculation - No MMS multiplier since we're using links
  const creditMultiplier = 1;

  // Call the callback when channel or attachments change (but not message)
  useEffect(() => {
    if (onCharacterCountChange) {
      onCharacterCountChange({
        charactersUsed,
        characterLimit,
        segments,
        isUnicodeMessage,
        creditMultiplier,
        hasAttachments,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannel, hasAttachments, onCharacterCountChange]); // Only depend on channel and attachments, not message

  const insertVariableAtCursor = useCallback(
    (variable) => {
      const textarea = messageTextareaRef.current;
      if (!textarea) return;
      if (!variable) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = message || '';
      const newText = text.substring(0, start) + variable + text.substring(end);
      setMessage(newText);

      // Refocus and set cursor position
      setTimeout(() => {
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(
            start + variable.length,
            start + variable.length
          );
        }
      }, 0);
    },
    [message, setMessage]
  );

  // Expose insertVariableAtCursor to parent if callback provided
  useEffect(() => {
    if (onInsertVariable) {
      onInsertVariable(insertVariableAtCursor);
    }
  }, [onInsertVariable, insertVariableAtCursor]);

  // Function to generate attachment links text using template variables
  const generateAttachmentLinks = (files) => {
    if (!files || files.length === 0) return '';

    const links = files
      .map((file, index) => {
        // Use template variable that will be replaced by backend
        if (file.url) {
          return `\nView attachment: {{attachment_${index + 1}_url}}`;
        }
        return '';
      })
      .filter((link) => link)
      .join('');

    return links || '';
  };

  // Track previous attachment count to know when to update links
  const prevAttachmentCount = useRef(0);

  // Auto-append attachment links when attachments change
  useEffect(() => {
    if (!attachments || !setMessage) return;

    // Only update if attachment count actually changed
    if (attachments.length === prevAttachmentCount.current) return;

    prevAttachmentCount.current = attachments.length;

    // Get current message without any attachment links (both URL and template variable formats)
    const messageWithoutLinks = (message || '').replace(
      /\nView attachment: (\{\{attachment_\d+_url\}\}|https?:\/\/[^\s\n]+)/g,
      ''
    );

    // Add new attachment links if there are attachments
    const attachmentLinks = generateAttachmentLinks(attachments);

    // Update message with new links
    const newMessage = messageWithoutLinks + attachmentLinks;
    if (newMessage !== message) {
      setMessage(newMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments]); // Only depend on attachments changing, not message

  // Default placeholder messages
  const getPlaceholder = () => {
    if (customPlaceholder) return customPlaceholder;

    if (context === 'credit') {
      return selectedChannel === 'SMS'
        ? `Hi {{first_name}}, {{track_name}} has issued you a {{credit_amount}} credit. View events: ${eventsUrl}`
        : 'Great news {{first_name}}! {{track_name}} has issued you a {{credit_amount}} credit. Check out upcoming events where you can use it.';
    }

    return selectedChannel === 'SMS'
      ? "Hey {{first_name}}! Don't miss the Sprint Car Championship this Saturday at {{track_name}}. Gates open at 5PM! View details: tkhs.co/info"
      : "Don't miss the action at {{track_name}}! Gates open at 5PM with special pre-race activities.";
  };

  // Default preview messages
  const getDefaultPreview = () => {
    if (context === 'credit') {
      return selectedChannel === 'SMS'
        ? `Hi John, Thunder Valley has issued you a $25.00 credit. View events: ${eventsUrl}`
        : 'Great news John! Thunder Valley has issued you a $25.00 credit. Check out upcoming events where you can use it.';
    }

    return selectedChannel === 'SMS'
      ? "Hey John! Don't miss the Sprint Car Championship this Saturday at Thunder Valley. Gates open at 5PM! View details: tkhs.co/info"
      : "Don't miss the action at Thunder Valley! Gates open at 5PM with special pre-race activities.";
  };

  const getDefaultPushTitle = () => {
    if (context === 'credit') {
      return 'You received a credit!';
    }
    return 'Sprint Car Championship This Saturday!';
  };

  return (
    <>
      <ContentGrid hidePreview={hidePreview}>
        <LeftColumn>
          {availableChannels.length > 1 ? (
            <ChannelSection
              title={
                <>
                  <svg
                    width="16"
                    height="16"
                    fill={theme?.colors?.primary || '#FA4616'}
                    viewBox="0 0 16 16"
                    style={{ marginRight: '8px' }}
                  >
                    <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.5a1 1 0 0 0-.8.4l-1.9 2.533a1 1 0 0 1-1.6 0L5.3 12.4a1 1 0 0 0-.8-.4H2a2 2 0 0 1-2-2V2zm3.5 1a.5.5 0 0 0 0 1h9a.5.5 0 0 0 0-1h-9zm0 2.5a.5.5 0 0 0 0 1h9a.5.5 0 0 0 0-1h-9zm0 2.5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5z" />
                  </svg>
                  Channel
                </>
              }
            >
              <ChannelTabs>
                {availableChannels.includes('SMS') && (
                  <ChannelTab
                    active={selectedChannel === 'SMS'}
                    onClick={() => setSelectedChannel('SMS')}
                  >
                    SMS
                  </ChannelTab>
                )}
                {availableChannels.includes('Push') && (
                  <ChannelTab
                    active={selectedChannel === 'Push'}
                    onClick={() => setSelectedChannel('Push')}
                  >
                    <svg
                      width="14"
                      height="14"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                      style={{ marginRight: '4px' }}
                    >
                      <path d="M8 16a2 2 0 0 0 2-2H6a2 2 0 0 0 2 2zM8 1.918l-.797.161A4.002 4.002 0 0 0 4 6c0 .628-.134 2.197-.459 3.742-.16.767-.376 1.566-.663 2.258h10.244c-.287-.692-.502-1.49-.663-2.258C12.134 8.197 12 6.628 12 6a4.002 4.002 0 0 0-3.203-3.92L8 1.917zM14.22 12c.223.447.481.801.78 1H1c.299-.199.557-.553.78-1C2.68 10.2 3 6.88 3 6c0-2.42 1.72-4.44 4.005-4.901a1 1 0 1 1 1.99 0A5.002 5.002 0 0 1 13 6c0 .88.32 4.2 1.22 6z" />
                    </svg>
                    Push
                  </ChannelTab>
                )}
              </ChannelTabs>
            </ChannelSection>
          ) : null}

          {/* Render additional section if provided */}
          {additionalSection}

          <ComposeSection
            title={
              <>
                <svg
                  width="16"
                  height="16"
                  fill={theme?.colors?.primary || '#FA4616'}
                  viewBox="0 0 16 16"
                  style={{ marginRight: '8px' }}
                >
                  <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z" />
                </svg>
                Compose Message
              </>
            }
          >
            {selectedChannel === 'Push' && (
              <>
                <TitleInput
                  type="text"
                  value={
                    typeof pushTitle === 'string' && pushTitle !== 'null'
                      ? pushTitle
                      : ''
                  }
                  onChange={(e) => setPushTitle(e.target.value)}
                  placeholder={getDefaultPushTitle()}
                  maxLength={50}
                />
                <CharacterCount warning={(pushTitle || '').length > 40}>
                  Title: {(pushTitle || '').length} / 50 characters
                </CharacterCount>
              </>
            )}
            {context === 'credit' && message && message.trim() !== '' && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '8px',
                  padding: '8px 12px',
                  background: 'rgba(250, 70, 22, 0.1)',
                  borderRadius: '6px',
                  border: '1px solid rgba(250, 70, 22, 0.2)',
                }}
              >
                <svg
                  width="14"
                  height="14"
                  fill={theme?.colors?.primary || '#FA4616'}
                  viewBox="0 0 16 16"
                >
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                  <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z" />
                </svg>
                <span
                  style={{
                    fontSize: '12px',
                    color: theme?.colors?.primary || '#FA4616',
                    fontWeight: '500',
                  }}
                >
                  Tip: Click anywhere in the message below to customize it
                </span>
              </div>
            )}
            <MessageTextarea
              ref={messageTextareaRef}
              value={
                typeof message === 'string' && message !== 'null' ? message : ''
              }
              onChange={(e) => {
                const newMessage = e.target.value;
                setMessage(newMessage);

                // Call character count callback directly instead of in useEffect
                if (onCharacterCountChange) {
                  const newCharactersUsed = (newMessage || '').length;
                  const newIsUnicodeMessage =
                    selectedChannel === 'SMS' && hasUnicode(newMessage || '');

                  // Recalculate character limit based on new message
                  let newCharacterLimit;
                  if (selectedChannel === 'Push') {
                    newCharacterLimit = 250;
                  } else if (isMMSMessage) {
                    newCharacterLimit = 1600;
                  } else if (newIsUnicodeMessage) {
                    newCharacterLimit =
                      newCharactersUsed <= 70
                        ? 70
                        : 67 * Math.ceil(newCharactersUsed / 67);
                  } else {
                    newCharacterLimit =
                      newCharactersUsed <= 160
                        ? 160
                        : 153 * Math.ceil(newCharactersUsed / 153);
                  }

                  const newSegments =
                    selectedChannel === 'SMS'
                      ? calculateSMSSegments(newMessage || '', isMMSMessage)
                      : 0;

                  onCharacterCountChange({
                    charactersUsed: newCharactersUsed,
                    characterLimit: newCharacterLimit,
                    segments: newSegments,
                    isUnicodeMessage: newIsUnicodeMessage,
                    creditMultiplier,
                    hasAttachments,
                  });
                }
              }}
              placeholder={getPlaceholder()}
              maxLength={
                isMMSMessage ? 1600 : selectedChannel === 'SMS' ? 1600 : 250
              }
              style={selectedChannel === 'Push' ? { minHeight: '80px' } : {}}
            />
            {!hideCharacterCount && (
              <CharacterCount
                style={{ fontSize: '14px' }}
                warning={charactersUsed > characterLimit * 0.9}
              >
                {selectedChannel === 'SMS' ? (
                  <>
                    <span>
                      {charactersUsed} / {characterLimit} characters
                    </span>
                    {segments > 0 && (
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        • {segments} segment{segments > 1 ? 's' : ''} ={' '}
                        {segments} credit{segments > 1 ? 's' : ''}
                      </span>
                    )}
                    {isUnicodeMessage && (
                      <span style={{ color: '#F59E0B' }}>
                        {' '}
                        • Unicode (contains emoji/special characters)
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    Body: {charactersUsed} / {characterLimit} characters
                  </>
                )}
              </CharacterCount>
            )}

            <QuickVariables>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px',
                }}
              >
                <span
                  style={{
                    fontSize: '12px',
                    color: theme?.name === 'dark' ? '#CBD5E1' : '#64748B',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <span role="img" aria-label="lightning">
                    ⚡
                  </span>{' '}
                  Personalization Shortcuts
                  <HelpButton
                    onClick={() => setShowVariableEducation(true)}
                    aria-label="Learn about quick variables"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M5.496 6.033h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286a.237.237 0 0 0 .241.247m2.325 6.443c.61 0 1.029-.394 1.029-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94 0 .533.425.927 1.01.927z" />
                    </svg>
                  </HelpButton>
                </span>
                <div style={{ position: 'relative' }}>
                  <VariableButton
                    ref={emojiButtonRef}
                    onClick={() => setIsEmojiPickerOpen(!isEmojiPickerOpen)}
                    style={{ marginLeft: '8px' }}
                  >
                    <span role="img" aria-label="smiling face">
                      😊
                    </span>{' '}
                    Emoji
                  </VariableButton>
                  <EmojiPicker
                    isOpen={isEmojiPickerOpen}
                    onClose={() => setIsEmojiPickerOpen(false)}
                    onSelect={(emoji) => insertVariableAtCursor(emoji)}
                    position="top"
                  />
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap',
                }}
              >
                {variables.map((variable) => (
                  <VariableButton
                    key={variable.value}
                    onClick={() => {
                      if (variable.onClick) {
                        variable.onClick();
                      } else {
                        insertVariableAtCursor(variable.value);
                      }
                    }}
                  >
                    {variable.icon && (
                      <svg
                        width="12"
                        height="12"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                        style={{ marginRight: '4px' }}
                      >
                        <path d={variable.icon} />
                      </svg>
                    )}
                    {variable.label}
                  </VariableButton>
                ))}
              </div>
            </QuickVariables>

            {/* Attachment Upload */}
            {attachments !== undefined && setAttachments !== undefined && (
              <AttachmentUpload
                attachments={attachments}
                onAttachmentsChange={setAttachments}
                channel={selectedChannel}
                disabled={false}
                onError={onError}
              />
            )}
          </ComposeSection>
        </LeftColumn>

        {!hidePreview && (
          <RightColumn>
            <MessagePreview
              channel={selectedChannel}
              message={replaceVariables(message) || getDefaultPreview()}
              pushTitle={replaceVariables(pushTitle) || getDefaultPushTitle()}
              showTitle={true}
              compact={false}
              attachments={attachments}
            />
          </RightColumn>
        )}
      </ContentGrid>

      {/* Education Drawer for Personalization Shortcuts */}
      <EducationDrawer
        title="Understanding Personalization Shortcuts"
        isOpen={showVariableEducation}
        onClose={() => setShowVariableEducation(false)}
        showHelpButton={false}
      >
        <div style={{ marginBottom: '24px' }}>
          <h3
            style={{
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '12px',
            }}
          >
            What They Are
          </h3>
          <p style={{ fontSize: '14px', lineHeight: '1.5', margin: 0 }}>
            Personalization shortcuts are little codes you can drop into your
            message. When sent, they automatically fill in with the right
            details for each person — like their name, event, or discount code.
          </p>
          <p
            style={{
              fontSize: '14px',
              lineHeight: '1.5',
              margin: 0,
              marginTop: '10px',
            }}
          >
            <strong style={{ fontWeight: 700 }}>
              Think of them as "magic blanks"
            </strong>{' '}
            you fill in once — the system does the rest, personalizing for every
            customer automatically
          </p>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h3
            style={{
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '12px',
            }}
          >
            Shortcuts You Can Use
          </h3>
          <div style={{ fontSize: '13px', lineHeight: '1.8' }}>
            <div style={{ marginBottom: '6px' }}>
              <CodeBlock>{`{{first_name}}`}</CodeBlock> → Customer's first name
              (e.g., John)
            </div>
            <div style={{ marginBottom: '6px' }}>
              <CodeBlock>{`{{event_name}}`}</CodeBlock> → Event name
            </div>
            <div style={{ marginBottom: '6px' }}>
              <CodeBlock>{`{{event_date}}`}</CodeBlock> → Event date
            </div>
            <div style={{ marginBottom: '6px' }}>
              <CodeBlock>{`{{discount_code}}`}</CodeBlock> → Discount code
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h3
            style={{
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '16px',
            }}
          >
            How It Works
          </h3>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '14px',
                background: 'rgba(250, 70, 22, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(250, 70, 22, 0.15)',
              }}
            >
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: theme?.colors?.primary || '#FA4616',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '600',
                  flexShrink: 0,
                }}
              >
                1
              </div>
              <div style={{ flex: 1 }}>
                <strong
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                  }}
                >
                  Add a shortcut
                </strong>
                <div
                  style={{
                    fontSize: '13px',
                    color: '#94a3b8',
                    lineHeight: '1.5',
                  }}
                >
                  Example: Hi <CodeBlock>{`{{first_name}}`}</CodeBlock>, see you
                  at <CodeBlock>{`{{event_name}}`}</CodeBlock> on{' '}
                  <CodeBlock>{`{{event_date}}`}</CodeBlock>!
                </div>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '14px',
                background: 'rgba(250, 70, 22, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(250, 70, 22, 0.15)',
              }}
            >
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: theme?.colors?.primary || '#FA4616',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '600',
                  flexShrink: 0,
                }}
              >
                2
              </div>
              <div style={{ flex: 1 }}>
                <strong
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                  }}
                >
                  Send your message
                </strong>
                <div style={{ fontSize: '13px', color: '#94a3b8' }}>
                  The codes fill in with real info.
                </div>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '14px',
                background: theme?.colors?.success
                  ? `${theme.colors.success}0D`
                  : 'rgba(34, 197, 94, 0.05)',
                borderRadius: '8px',
                border: theme?.colors?.success
                  ? `1px solid ${theme.colors.success}26`
                  : '1px solid rgba(34, 197, 94, 0.15)',
              }}
            >
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: theme?.colors?.success || '#22C55E',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '600',
                  flexShrink: 0,
                }}
              >
                ✓
              </div>
              <div style={{ flex: 1 }}>
                <strong
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                  }}
                >
                  Customer sees
                </strong>
                <div
                  style={{
                    fontSize: '13px',
                    color: theme?.colors?.success || '#22C55E',
                    fontWeight: '500',
                  }}
                >
                  {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
                  🏁 Hi Sarah, see you at Summer Nationals on July 10!
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            padding: '16px',
            background: 'rgba(250, 70, 22, 0.05)',
            borderRadius: '8px',
            border: '1px solid rgba(250, 70, 22, 0.2)',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '20px 1fr',
              columnGap: '6px',
              rowGap: '4px',
              alignItems: 'start',
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 16 16"
              fill={theme?.colors?.primary || '#fa4616'}
              style={{ marginTop: '2px' }}
            >
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
              <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
            </svg>
            <div>
              <p
                style={{
                  margin: '0 0 12px 0',
                  fontSize: '13px',
                  fontWeight: '600',
                }}
              >
                Pro Tips
              </p>
            </div>
            <div
              style={{
                gridColumn: '1',
                color: theme?.colors?.primary || '#FA4616',
                fontSize: '14px',
                lineHeight: '1.6',
                textAlign: 'center',
              }}
            >
              •
            </div>
            <div
              style={{ gridColumn: '2', fontSize: '12px', lineHeight: '1.6' }}
            >
              Always preview your message to see how variables will appear
            </div>
            <div
              style={{
                gridColumn: '1',
                color: theme?.colors?.primary || '#FA4616',
                fontSize: '14px',
                lineHeight: '1.6',
                textAlign: 'center',
              }}
            >
              •
            </div>
            <div
              style={{ gridColumn: '2', fontSize: '12px', lineHeight: '1.6' }}
            >
              Use emojis alongside variables for more engaging messages
            </div>
            <div
              style={{
                gridColumn: '1',
                color: theme?.colors?.primary || '#FA4616',
                fontSize: '14px',
                lineHeight: '1.6',
                textAlign: 'center',
              }}
            >
              •
            </div>
            <div
              style={{ gridColumn: '2', fontSize: '12px', lineHeight: '1.6' }}
            >
              Keep messages concise - SMS has a 160 character limit
            </div>
            <div
              style={{
                gridColumn: '1',
                color: theme?.colors?.primary || '#FA4616',
                fontSize: '14px',
                lineHeight: '1.6',
                textAlign: 'center',
              }}
            >
              •
            </div>
            <div
              style={{ gridColumn: '2', fontSize: '12px', lineHeight: '1.6' }}
            >
              Test your messages with different customer data
            </div>
          </div>
        </div>
      </EducationDrawer>
    </>
  );
};

MessageComposer.propTypes = {
  selectedChannel: PropTypes.string.isRequired,
  setSelectedChannel: PropTypes.func.isRequired,
  message: PropTypes.string,
  setMessage: PropTypes.func.isRequired,
  pushTitle: PropTypes.string,
  setPushTitle: PropTypes.func,
  variables: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      icon: PropTypes.string,
      onClick: PropTypes.func,
    })
  ),
  customPlaceholder: PropTypes.string,
  replaceVariables: PropTypes.func.isRequired,
  context: PropTypes.oneOf(['campaign', 'credit']),
  attachments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      size: PropTypes.number.isRequired,
      sizeFormatted: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      isImage: PropTypes.bool,
      isPDF: PropTypes.bool,
    })
  ),
  setAttachments: PropTypes.func,
  availableChannels: PropTypes.arrayOf(PropTypes.oneOf(['SMS', 'Push'])),
  onCharacterCountChange: PropTypes.func,
  onInsertVariable: PropTypes.func,
  hidePreview: PropTypes.bool,
  additionalSection: PropTypes.node,
  onError: PropTypes.func,
  hideCharacterCount: PropTypes.bool,
};

MessageComposer.defaultProps = {
  variables: [],
  context: 'campaign',
  message: '',
  pushTitle: '',
};

export default MessageComposer;
